import React, { useState } from "react";
import {
  BadgeHelp,
  ChevronDown,
  Code,
  Headset,
  Info,
  Laptop,
  Menu,
  Monitor,
  Shield,
  Smartphone,
  Star,
  X
} from "lucide-react";
import { Link } from "react-router-dom";
import logo from "./logo.svg";

const menuItems = [
  { label: "Quiénes somos", path: "/info/quienes-somos", icon: Info },
  { label: "Preguntas frecuentes", path: "/info/preguntas-frecuentes", icon: BadgeHelp },
  { label: "Política de privacidad", path: "/info/politica-privacidad", icon: Shield },
  { label: "Soporte técnico", path: "/info/soporte-tecnico", icon: Headset },
  { label: "Recomendaciones", path: "/info/recomendaciones", icon: Star }
];

const initialTestimonials = [
  {
    name: "Camila Rojas",
    role: "Directora Académica",
    comment: "Excelente experiencia. Nos ayudaron a aterrizar la idea y el resultado final fue muy profesional."
  },
  {
    name: "Felipe Morales",
    role: "Jefe de Producto",
    comment: "Destaco la comunicación del equipo. Siempre hubo claridad en avances, tiempos y mejoras sugeridas."
  },
  {
    name: "Valentina Soto",
    role: "Coordinadora de Contenidos",
    comment: "El soporte post-lanzamiento fue clave para nosotros. Responden rápido y con soluciones concretas."
  },
  {
    name: "Rodrigo Pizarro",
    role: "Gerente Comercial",
    comment: "Quedamos muy conformes. Mejoró nuestra presencia digital y la experiencia de nuestros usuarios."
  },
  {
    name: "Daniela Fuentes",
    role: "Líder de Operaciones",
    comment: "Se nota el enfoque técnico y de negocio. Entregaron una solución útil, estable y fácil de usar."
  }
];

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const testimonialTrack = [...initialTestimonials, ...initialTestimonials];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-yellow-300/40 dark:bg-slate-950 dark:text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-4 sm:px-5">
          <a href="#inicio" className="flex items-center gap-3">
            <img src={logo} alt="Logo Cynaxia" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-wide text-amber-500 dark:text-amber-400">Cynaxia</span>
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="rounded-xl border border-slate-300 p-2 text-slate-700 transition hover:border-amber-500 hover:text-amber-600 dark:border-slate-700 dark:text-slate-100 dark:hover:border-amber-400 dark:hover:text-amber-300"
            aria-label="Abrir menú"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 dark:bg-black/60">
          <div className="h-full w-full max-w-sm overflow-y-auto border-l border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Cynaxia" className="h-8 w-8" />
                <span className="font-semibold text-amber-500 dark:text-amber-400">Cynaxia</span>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Cerrar menú"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-amber-50 hover:text-amber-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-amber-300"
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <section id="inicio" className="relative overflow-hidden px-4 pb-20 pt-16 text-center sm:pt-24">
        <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-200/50 blur-[120px] dark:bg-blue-900/30" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-amber-200/50 blur-[100px] dark:bg-amber-600/20" />
        <div className="relative z-10 mx-auto flex min-h-[70vh] max-w-4xl flex-col items-center justify-center">
          <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Bienvenido a{" "}
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent dark:from-amber-300 dark:to-yellow-500">
              Cynaxia
            </span>
          </h1>
          <p className="mb-10 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg md:text-2xl dark:text-slate-300">
            Software, apps y soluciones digitales a medida diseñadas para el futuro.
          </p>
          <a
            href="#servicios"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3 font-bold text-white shadow-lg shadow-amber-500/30 transition hover:scale-105 hover:bg-amber-400 dark:bg-amber-500 dark:text-slate-950 dark:hover:bg-amber-400"
          >
            Conoce nuestros servicios
            <ChevronDown className="h-5 w-5" />
          </a>
        </div>
      </section>

      <section id="servicios" className="relative border-t border-slate-200 bg-white py-16 dark:border-slate-800 dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="mb-12 text-center text-3xl font-bold text-slate-900 dark:text-slate-100">Nuestros Servicios</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/servicios/sitios-web"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-blue-400 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-400/60"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition-transform group-hover:scale-110 dark:bg-blue-500/15 dark:text-blue-300">
                <Laptop className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">Sitios Web</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">Explora nuestra colección de sitios web modernos.</p>
            </Link>

            <Link
              to="/servicios/apps-moviles"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-green-400 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-green-400/60"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-green-100 text-green-600 transition-transform group-hover:scale-110 dark:bg-green-500/15 dark:text-green-300">
                <Smartphone className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">Apps Móviles</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">Soluciones nativas y multiplataforma de alto rendimiento.</p>
            </Link>

            <Link
              to="/servicios/software-medida"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-red-400 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-red-400/60"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-red-100 text-red-600 transition-transform group-hover:scale-110 dark:bg-red-500/15 dark:text-red-300">
                <Code className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">Software a Medida</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">Desarrollo personalizado para necesidades específicas.</p>
            </Link>

            <Link
              to="/servicios/apps-escritorio"
              className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-amber-400 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-amber-400/60"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-amber-100 text-amber-600 transition-transform group-hover:scale-110 dark:bg-amber-500/15 dark:text-amber-300">
                <Monitor className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">Apps de Escritorio</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">Herramientas potentes para tu flujo de trabajo diario.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50 py-14 dark:border-slate-800 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h3 className="mb-6 text-center text-2xl font-bold text-slate-900 dark:text-slate-100">
            Lo que opinan nuestros clientes
          </h3>

          {/* Mobile: scroll horizontal natural (sin recortes) */}
          <div className="md:hidden -mx-4 overflow-x-auto px-4 pb-2">
            <div className="flex w-max gap-3">
              {initialTestimonials.map((item, index) => (
                <article
                  key={`mobile-${item.name}-${index}`}
                  className="w-[280px] shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
                >
                  <p className="text-sm text-slate-700 dark:text-slate-200">"{item.comment}"</p>
                  <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-100">{item.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{item.role}</p>
                </article>
              ))}
            </div>
          </div>

          {/* Desktop/Tablet: carrusel infinito sutil */}
          <div className="carousel-viewport hidden overflow-hidden md:block">
            <div className="carousel-track flex w-max gap-4">
              {testimonialTrack.map((item, index) => (
                <article
                  key={`desktop-${item.name}-${index}`}
                  className="w-[300px] shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
                >
                  <p className="text-sm text-slate-700 dark:text-slate-200">"{item.comment}"</p>
                  <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-100">{item.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{item.role}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-slate-200 bg-slate-100 py-8 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
        <p>
          © {new Date().getFullYear()} Cynaxia. Todos los derechos reservados{" "}
          <Link to="/privacy" className="cursor-pointer underline hover:text-slate-900 dark:hover:text-white">
            Política de privacidad
          </Link>
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;